/**
 * PNR Parser Utility
 * Parses common GDS (Global Distribution System) PNR formats
 * Supports Sabre, Amadeus, and Galileo formats
 */

// Month abbreviations mapping
const MONTHS = {
  'JAN': 1, 'FEB': 2, 'MAR': 3, 'APR': 4, 'MAY': 5, 'JUN': 6,
  'JUL': 7, 'AUG': 8, 'SEP': 9, 'OCT': 10, 'NOV': 11, 'DEC': 12
};

// Class code to readable name mapping
const CLASS_MAP = {
  'F': 'First',
  'A': 'First',
  'J': 'Business',
  'C': 'Business',
  'D': 'Business',
  'I': 'Business',
  'W': 'Premium Economy',
  'Y': 'Economy',
  'B': 'Economy',
  'H': 'Economy',
  'K': 'Economy',
  'L': 'Economy',
  'M': 'Economy',
  'N': 'Economy',
  'Q': 'Economy',
  'T': 'Economy',
  'V': 'Economy',
  'X': 'Economy',
  'E': 'Economy'
};

/**
 * Parse date string (e.g., "26OCT", "26OCT22", "26OCT2022")
 */
function parseDate(dateStr) {
  if (!dateStr) return null;
  
  const currentYear = new Date().getFullYear();
  const match = dateStr.match(/(\d{1,2})([A-Z]{3})(\d{2,4})?/);
  
  if (!match) return null;
  
  const day = parseInt(match[1], 10);
  const monthStr = match[2];
  const yearStr = match[3];
  
  const month = MONTHS[monthStr];
  if (!month) return null;
  
  let year;
  if (yearStr) {
    if (yearStr.length === 2) {
      year = 2000 + parseInt(yearStr, 10);
    } else {
      year = parseInt(yearStr, 10);
    }
  } else {
    year = currentYear;
  }
  
  return new Date(year, month - 1, day);
}

/**
 * Parse time string (e.g., "1945" -> "19:45")
 */
function parseTime(timeStr) {
  if (!timeStr || timeStr.length < 3) return null;
  
  const hours = parseInt(timeStr.substring(0, 2), 10);
  const minutes = parseInt(timeStr.substring(2, 4), 10);
  
  if (isNaN(hours) || isNaN(minutes)) return null;
  if (hours > 23 || minutes > 59) return null;
  
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
}

/**
 * Extract airport codes from string
 */
function extractAirports(text) {
  // First, check for combined airport codes (e.g., "WLGAKL" = WLG + AKL)
  const combinedPattern = /\b([A-Z]{6})\b/g;
  const combinedMatches = text.match(combinedPattern);
  
  if (combinedMatches && combinedMatches.length > 0) {
    // Filter out codes that contain common non-airport patterns
    const excludePatterns = ['FLIGHT', 'TICKET'];
    const validCombined = combinedMatches.filter(code => 
      !excludePatterns.some(pattern => code.includes(pattern))
    );
    
    if (validCombined.length > 0) {
      const combined = validCombined[0];
      return { 
        from: combined.substring(0, 3), 
        to: combined.substring(3, 6) 
      };
    }
  }
  
  // Fall back to looking for separate 3-letter uppercase airport codes
  const airportPattern = /\b([A-Z]{3})\b/g;
  const matches = text.match(airportPattern);
  
  if (!matches || matches.length < 2) return { from: null, to: null };
  
  // Filter out common non-airport codes
  const excludeCodes = ['PNR', 'HK1', 'HK2', 'HK3', 'HK4', 'HK5', 'HK6', 'HK7', 'HK8', 'HK9', 'OCT', 'NOV', 'DEC', 'JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP'];
  const airports = matches.filter(code => !excludeCodes.includes(code));
  
  if (airports.length >= 2) {
    return { from: airports[0], to: airports[1] };
  }
  
  return { from: null, to: null };
}

/**
 * Extract airline code and flight number
 */
function extractFlightInfo(text) {
  // Pattern: Airline code (2-3 letters) followed by flight number (3-4 digits)
  // Examples: "NZ 456", "EK204", "AA1234"
  const patterns = [
    /\b([A-Z]{2,3})\s*(\d{3,4})\b/,  // "NZ 456" or "EK204"
    /\b([A-Z]{2})\/([A-Z]\d+)\b/     // "NZ/W268NH"
  ];
  
  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match) {
      return {
        airline: match[1],
        flightNumber: match[2] || match[1] + match[2]
      };
    }
  }
  
  return { airline: null, flightNumber: null };
}

/**
 * Extract class code
 */
function extractClass(text) {
  // Look for single letter class codes
  const classPattern = /\b([A-Z])\b/;
  const match = text.match(classPattern);
  
  if (match && CLASS_MAP[match[1]]) {
    return match[1];
  }
  
  return null;
}

/**
 * Try to parse structured GDS formats (Amadeus, Sabre, Galileo)
 * Common formats:
 * - Amadeus: "1 NZ456 Y 26OCT WLG AKL HK1 1945 2050"
 * - Sabre: "1 NZ 456 Y 26OCT WLG AKL HK1 1945 2050"
 * - Galileo: Similar to Amadeus
 */
function parseStructuredFormat(text) {
  // Pattern for structured format: [segment] [airline] [flight] [class] [date] [from] [to] [status] [dep_time] [arr_time]
  // Examples:
  // "1 NZ456 Y 26OCT WLG AKL HK1 1945 2050"
  // "2 NZ 456 H 26OCT 5 WLGAKL HK1 1945 2050"
  const structuredPattern = /(\d+)\s+([A-Z]{2,3})\s*(\d{3,4})\s+([A-Z])\s+(\d{1,2}[A-Z]{3}\d{0,4})\s+([A-Z]{3,6})\s+([A-Z]{3})\s+([A-Z0-9]+)\s+(\d{4})\s+(\d{4})/i;
  const match = text.match(structuredPattern);
  
  if (match) {
    const airline = match[2];
    const flightNumber = match[3];
    const classCode = match[4];
    const dateStr = match[5];
    const fromAirport = match[6].length === 6 ? match[6].substring(0, 3) : match[6];
    const toAirport = match[6].length === 6 ? match[6].substring(3, 6) : match[7];
    const depTime = match[9];
    const arrTime = match[10];
    
    return {
      airline,
      flightNumber,
      classCode,
      departureDate: parseDate(dateStr),
      flyFrom: fromAirport,
      flyTo: toAirport,
      departureTime: parseTime(depTime),
      arrivalTime: parseTime(arrTime)
    };
  }
  
  return null;
}

/**
 * Main PNR parser function
 * @param {string} pnrText - Raw PNR text
 * @returns {Object} Parsed flight data or null if parsing fails
 */
export function parsePNR(pnrText) {
  if (!pnrText || typeof pnrText !== 'string') {
    return { error: 'Invalid PNR input' };
  }
  
  const trimmed = pnrText.trim();
  if (trimmed.length < 10) {
    return { error: 'PNR text too short' };
  }
  
  // First, try to parse as structured GDS format (Amadeus, Sabre, Galileo)
  const structuredResult = parseStructuredFormat(trimmed);
  if (structuredResult) {
    return {
      ...structuredResult,
      arrivalDate: structuredResult.departureDate, // Default to same day if not specified
      class: structuredResult.classCode ? CLASS_MAP[structuredResult.classCode] : 'Economy',
      raw: trimmed
    };
  }
  
  // Fall back to pattern matching for less structured formats
  const parts = trimmed.split(/[\s\/]+/).filter(p => p.length > 0);
  
  if (parts.length < 5) {
    return { error: 'PNR format not recognized' };
  }
  
  // Extract flight information
  const flightInfo = extractFlightInfo(trimmed);
  const airports = extractAirports(trimmed);
  const classCode = extractClass(trimmed);
  
  // Find dates in the text
  const datePattern = /(\d{1,2}[A-Z]{3}\d{0,4})/g;
  const dateMatches = trimmed.match(datePattern);
  let departureDate = null;
  let arrivalDate = null;
  
  if (dateMatches && dateMatches.length > 0) {
    departureDate = parseDate(dateMatches[0]);
    if (dateMatches.length > 1) {
      arrivalDate = parseDate(dateMatches[1]);
    }
  }
  
  // Find times in the text (4-digit numbers that could be times)
  const timePattern = /\b(\d{4})\b/g;
  const timeMatches = trimmed.match(timePattern);
  let departureTime = null;
  let arrivalTime = null;
  
  if (timeMatches && timeMatches.length > 0) {
    // Filter out dates that look like times (e.g., "2022")
    const validTimes = timeMatches.filter(t => {
      const num = parseInt(t, 10);
      return num >= 0 && num <= 2359 && num % 100 < 60;
    });
    
    if (validTimes.length > 0) {
      departureTime = parseTime(validTimes[0]);
      if (validTimes.length > 1) {
        arrivalTime = parseTime(validTimes[1]);
      }
    }
  }
  
  // Build result object
  const result = {
    airline: flightInfo.airline,
    flightNumber: flightInfo.flightNumber,
    flyFrom: airports.from,
    flyTo: airports.to,
    departureDate: departureDate,
    arrivalDate: arrivalDate || departureDate,
    departureTime: departureTime,
    arrivalTime: arrivalTime,
    classCode: classCode,
    class: classCode ? CLASS_MAP[classCode] : 'Economy',
    raw: trimmed
  };
  
  // Validate that we have at least some essential data
  if (!flightInfo.airline && !airports.from) {
    return { error: 'Could not extract flight information from PNR' };
  }
  
  return result;
}

/**
 * Format parsed PNR data for display
 */
export function formatPNRData(parsedData) {
  if (parsedData.error) {
    return { error: parsedData.error };
  }
  
  const formatDate = (date) => {
    if (!date) return 'N/A';
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };
  
  return {
    airline: parsedData.airline || 'N/A',
    flightNumber: parsedData.flightNumber || 'N/A',
    route: parsedData.flyFrom && parsedData.flyTo 
      ? `${parsedData.flyFrom} → ${parsedData.flyTo}` 
      : 'N/A',
    departure: parsedData.departureDate 
      ? `${formatDate(parsedData.departureDate)} ${parsedData.departureTime || ''}`.trim()
      : 'N/A',
    arrival: parsedData.arrivalDate 
      ? `${formatDate(parsedData.arrivalDate)} ${parsedData.arrivalTime || ''}`.trim()
      : 'N/A',
    class: parsedData.class || 'Economy',
    raw: parsedData.raw
  };
}

export default { parsePNR, formatPNRData };

